/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../../_metronic/layout/core/PageData'
import { DropdownMenu, SettingModalOption, SettingOption } from '../../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../../common/component/CustomInputRenderer'
import { SubSectionHeader } from '../../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../../common/component/FilePicker'
import { IAttachment } from '../../../media/Media.model'
import { DeleteConfirmationModal } from '../../../../common/component/modal/DeleteConfirmationModal'
import { MessageToast, MessageType } from '../../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { parseLocalDate } from '../../../../util/Locale'
import { renderIdentifier } from '../../../../util/Constants'
import { useTaskDeleteByIdMutation, useTaskGetByIdMutation } from '../Task.api'
import { Spinner } from '../../../../common/component/Spinner'
import { TicketEmbedered } from '../../ticket/components/TicketEmbedered'
import { ContactEmbedered } from '../../../contact/components/ContactEmbedered'
import { IATask } from '../Task.model'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'

export const TaskDetailsPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {ticketId, taskId} = useParams()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.TICKETS'}),
      path: '/tickets',
      isSeparator: false,
      isActive: false,
    },
    {
      title: `/`,
      path: ``,
      isSeparator: true,
      isActive: false,
    },
    {
      title: `${ticketId}`,
      path: `/tickets/${ticketId}`,
      isSeparator: false,
      isActive: false,
    },
  ]
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.EDIT'}),
      icon: 'bi-pencil',
      action: () => navigate(`/tickets/${ticketId}/tasks/${taskId}/edit`),
    },
  ]
  const removeSettingOption: SettingModalOption = {
    label: intl.formatMessage({id: 'UTIL.DELETE'}),
    targetModal: '#task_delete_confirmation_modal'
  }
  
  const didFetchData = useRef(false)
  const didRequestDelete = useRef(false)
  const [data, setData] = useState<IATask | undefined>()
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [openDropDown, setOpenDropDown] = useState(false)

  const [taskGetById] = useTaskGetByIdMutation()
  const [taskDeleteById] = useTaskDeleteByIdMutation()

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchData = async (id) => {
      try {
        if (!didFetchData.current) {
          setIsDataLoading(true)
          const response: any = await taskGetById({id})
          if (!response || response.error) {
            setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
          } else {
            const data: IATask = response.data
            setData(data)
          }
        }
      } catch (e: any) {
        setLoadError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsDataLoading(false)
      }
    }

    if (taskId) {
      fetchData(taskId)
    } 
  }, [])

  const deleteConfirmOnClickHandler = async () => {
    try {
      if (taskId && !didRequestDelete.current) {
        setIsDataLoading(true)
        const response: any = await taskDeleteById(taskId)
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          navigate(`/tickets/${ticketId}`)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsDataLoading(false)
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <div className='d-flex flex-row'>
            <div className='d-flex flex-column flex-row-fluid'>
              <div className='d-flex flex-row flex-column-fluid'>
                <div className='d-flex flex-row-fluid flex-start'>
                  {data && data.task && (
                    <div className='d-flex flex-column'>
                      <span className='text-gray-800 fs-2 fw-bolder'>{`${data.task.title}`}</span>
                      <span className='fw-bolder text-primary me-1 fs-5'>
                        {renderIdentifier(data.task.sequence)}
                      </span>
                    </div>
                  )}
                </div>

                <div className='d-flex flex-row-auto flex-start ms-5'>
                  {data && data.task && (
                    <>
                      <button
                        className='btn btn-sm btn-light-primary me-2'
                        onClick={() => setOpenDropDown((prev) => !prev)}
                        disabled={isDataLoading}
                      >
                        {isDataLoading ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle'></span>
                          </span>
                        ) : (
                          <i className='bi bi-three-dots fs-5'></i>
                        )}
                      </button>
                      <DropdownMenu
                        visible={openDropDown}
                        setOpenDropDown={setOpenDropDown}
                        settingOptions={settingOptions}
                        deleteOption={removeSettingOption}
                      ></DropdownMenu>
                    </>
                  )}

                  <button
                    className='btn btn-sm btn-light'
                    onClick={() => navigate(`/tickets/${ticketId}`)}
                  >
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isDataLoading ? (
            <Spinner />
          ) : !data || !data.task ? (
            <div className='row'>
              <div className='d-flex flex-row-fluid flex-start mt-10'>
                <span className='text-danger '>
                  <div className='d-flex flex-row justify-content-start align-items-center'>
                    <span className='fs-6 me-1'>
                      <i className='text-danger me-1 bi bi-exclamation-octagon' />
                      {intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'})}
                    </span>
                  </div>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <TicketEmbedered ticketId={ticketId!} />
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.CREATED_DATE'})}
                            value={
                              data.task?.createdAt
                                ? parseLocalDate(
                                    data.task.createdAt,
                                    intl.formatMessage({id: 'I18N.LOCALE'})
                                  )
                                : ''
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                            value={
                              data.task?.updatedAt
                                ? parseLocalDate(
                                    data.task.updatedAt,
                                    intl.formatMessage({id: 'I18N.LOCALE'})
                                  )
                                : ''
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ID'})}
                            value={`${data.task?.sequence}`}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.TITLE'})}
                            value={data.task?.title}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.STATUS'})}
                            value={intl.formatMessage({
                              id: `DROPDOWN.TASK.STATUS.${data.task?.statusCode}`,
                            })}
                          ></CustomInputRenderer>

                          {data.task?.startedAt && (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.STARTED_DATE_TIME'})}
                              object={
                                <>
                                  <span className='fs-6 mx-3 text-dark'>
                                    {data.task?.startedAt
                                      ? `${parseLocalDate(
                                          data.task.startedAt,
                                          intl.formatMessage({id: 'I18N.LOCALE'})
                                        )}`
                                      : ''}
                                  </span>
                                  <IconLabelContent
                                    title={`${data.task?.startingTime} ${intl.formatMessage({
                                      id: 'UTIL.MINUTES',
                                    })}`}
                                    icon={'bi bi-info-lg'}
                                    active={true}
                                    activeColor={'text-primary'}
                                  />
                                </>
                              }
                            ></CustomInputRenderer>
                          )}

                          {data.task?.assignedTo && (
                            <>
                              <ContactEmbedered
                                label={intl.formatMessage({id: 'UTIL.ASSIGNED_TO'})}
                                contact={data.task?.assignedTo}
                              />
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.ASSIGNED_DATE_TIME'})}
                                object={
                                  <>
                                    <span className='fs-6 mx-3 text-dark'>
                                      {data.task?.assignedAt
                                        ? `${parseLocalDate(
                                            data.task.assignedAt,
                                            intl.formatMessage({id: 'I18N.LOCALE'})
                                          )}`
                                        : ''}
                                    </span>
                                    <IconLabelContent
                                      title={`${data.task?.assignationTime} ${intl.formatMessage({
                                        id: 'UTIL.MINUTES',
                                      })}`}
                                      icon={'bi bi-info-lg'}
                                      active={true}
                                      activeColor={'text-primary'}
                                    />
                                  </>
                                }
                              ></CustomInputRenderer>
                            </>
                          )}

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.DESCRIPTION'})}
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                value={data.task?.description}
                                disabled={true}
                              />
                            }
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.DIAGNOSTIC_AND_RESOLUTION'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          {data.task?.arrivalAddress && (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ARRIVAL_ADDRESS'})}
                              value={data.task?.arrivalAddress}
                            ></CustomInputRenderer>
                          )}

                          {data.task?.arrivedAt && (
                            <CustomInputRenderer
                              label={intl.formatMessage({id: 'UTIL.ARRIVAL_DATE_TIME'})}
                              object={
                                <>
                                  <span className='fs-6 mx-3 text-dark'>
                                    {data.task?.arrivedAt
                                      ? `${parseLocalDate(
                                          data.task.arrivedAt,
                                          intl.formatMessage({id: 'I18N.LOCALE'})
                                        )}`
                                      : ''}
                                  </span>
                                  <IconLabelContent
                                    title={`${data.task?.arrivalTime} ${intl.formatMessage({
                                      id: 'UTIL.MINUTES',
                                    })}`}
                                    icon={'bi bi-info-lg'}
                                    active={true}
                                    activeColor={'text-primary'}
                                  />
                                </>
                              }
                            ></CustomInputRenderer>
                          )}

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.DIAGNOSTIC'})}
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                value={data.task?.diagnostic}
                                disabled={true}
                              />
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'TICKET.RESOLUTION'})}
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                value={data.task?.resolution}
                                disabled={true}
                              />
                            }
                          ></CustomInputRenderer>

                          {data.task?.resolutionTime && (
                            <>
                              <CustomInputRenderer
                                label={intl.formatMessage({id: 'UTIL.RESOLUTION_DATE_TIME'})}
                                object={
                                  <>
                                    <span className='fs-6 mx-3 text-dark'>
                                      {data.task?.resolvedAt
                                        ? `${parseLocalDate(
                                            data.task.resolvedAt,
                                            intl.formatMessage({id: 'I18N.LOCALE'})
                                          )}`
                                        : ''}
                                    </span>
                                    <IconLabelContent
                                      title={`${data.task?.resolutionTime} ${intl.formatMessage({
                                        id: 'UTIL.MINUTES',
                                      })}`}
                                      icon={'bi bi-info-lg'}
                                      active={true}
                                      activeColor={'text-primary'}
                                    />
                                  </>
                                }
                              ></CustomInputRenderer>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.METRICS'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                            value={`${data.task?.totalAttachments || 0}`}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div> */}
                  </div>

                  {data.media && data.media.length > 0 && (
                    <div className='row mb-5'>
                      <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ATTACHMENTS'})} />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <FilePicker
                            loading={isDataLoading}
                            uploadedFiles={data.media?.map((media) => {
                              const file: IAttachment = {
                                id: media.id,
                                fileName: media.fileName,
                                temporalPath: media.temporalPath,
                                path: media.path,
                                size: media.size,
                                contentType: media.contentType,
                                comments: media.comments,
                                status: media.status,
                                processing: false,
                                uploaded: true,
                              }
                              return file
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DeleteConfirmationModal
                id='task_delete_confirmation_modal'
                title={intl
                  .formatMessage({id: 'UTIL.DELETE_CONFIRMATION.TITLE'})
                  .replace(/%s/g, intl.formatMessage({id: 'UTIL.TASK'}))}
                description={`${data.task?.title} - #${data.task?.sequence}`}
                confirmOnClickHandler={deleteConfirmOnClickHandler}
              ></DeleteConfirmationModal>
            </>
          )}
        </div>
      </div>
    </>
  )
}
